import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { MapPin, Plus, X, Plane, ChevronDown, AlertTriangle, RefreshCw } from "lucide-react";

const CLASS_OPTIONS = [
  { value: "economy_only", labelKey: "decisions.economyOnly" },
  { value: "economy_business", labelKey: "decisions.economyBusiness" },
  { value: "full_three_class", labelKey: "decisions.fullThreeClass" },
];

// Range compatibility: which aircraft types can fly which distance categories
const RANGE_COMPAT = {
  short: ["turboprop", "used_narrowbody", "new_narrowbody", "widebody"],
  medium: ["used_narrowbody", "new_narrowbody", "widebody"],
  long: ["widebody"],
};

function isCompatible(aircraftType, distanceCategory) {
  if (!distanceCategory) return true;  // assume compatible if unknown
  return (RANGE_COMPAT[distanceCategory] || []).includes(aircraftType);
}

function AircraftMultiSelect({ selectedIds, allAircraft, distCat, assignedElsewhere, typeNames, onToggle, testIdPrefix }) {
  const { t } = useTranslation();
  const selectedAircraft = allAircraft.filter(a => selectedIds.includes(a.id));

  return (
    <div className="space-y-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full h-auto min-h-[32px] px-2 py-1.5 text-xs rounded-md border border-input bg-transparent shadow-sm hover:bg-accent/50 transition-colors text-left"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <span className="truncate text-slate-500">
              {selectedIds.length === 0
                ? t("decisions.selectAircraft")
                : `${selectedIds.length} ${t("decisions.aircraftSelected")}`}
            </span>
            <ChevronDown className="w-3.5 h-3.5 ml-1 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 px-1">{t("decisions.dedicatedCapacity")}</p>
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {allAircraft.map(ac => {
              const compat = isCompatible(ac.type, distCat);
              const elseRoute = assignedElsewhere[ac.id];
              const checked = selectedIds.includes(ac.id);
              const disabled = !compat || (!!elseRoute && !checked);
              return (
                <label
                  key={ac.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"} ${checked ? "bg-sky-50" : ""}`}
                  data-testid={`${testIdPrefix}-option-${ac.id}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={() => !disabled && onToggle(ac.id)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="font-medium">{ac.id}</span>
                  <span className="text-slate-400">({typeNames[ac.type] || ac.type})</span>
                  {!compat && <span className="text-rose-500 ml-auto text-[10px]">{t("decisions.outOfRange")}</span>}
                  {compat && elseRoute && !checked && <span className="text-amber-500 ml-auto text-[10px]">{elseRoute}</span>}
                </label>
              );
            })}
            {allAircraft.length === 0 && <p className="text-xs text-slate-400 text-center py-2">{t("decisions.noAircraft")}</p>}
          </div>
        </PopoverContent>
      </Popover>
      {/* Selected aircraft badges */}
      {selectedAircraft.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${testIdPrefix}-badges`}>
          {selectedAircraft.map(ac => (
            <Badge key={ac.id} variant="secondary" className="text-[10px] h-5 gap-1 pl-1.5 pr-0.5">
              {ac.id} ({typeNames[ac.type] || ac.type})
              <button
                type="button"
                className="ml-0.5 rounded-full hover:bg-slate-300/50 p-0.5 transition-colors"
                onClick={() => onToggle(ac.id)}
                data-testid={`${testIdPrefix}-remove-${ac.id}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RoutesSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  const [allRoutes, setAllRoutes] = useState([]);
  const [hubRoutes, setHubRoutes] = useState(null);
  const [routesError, setRoutesError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const hasHub = !!team?.hub_id;

  const fetchRoutes = useCallback(async () => {
    if (!team?.id) return;
    setRetrying(true);
    try {
      if (hasHub) {
        const res = await axios.get(`${API}/teams/${team.id}/hub-routes`);
        setHubRoutes(res.data);
      } else {
        const res = await axios.get(`${API}/reference/routes`);
        setAllRoutes(res.data);
      }
      setRoutesError(false);
    } catch {
      setRoutesError(true);
    } finally {
      setRetrying(false);
    }
  }, [team?.id, hasHub]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const activeRoutes = team?.active_routes || [];
  const aircraft = team?.aircraft || [];
  const routeConfig = decision.route_config || {};
  const routesToClose = decision.routes_close || [];
  const routesToOpen = decision.routes_open || [];

  // Available routes (legacy mode)
  const openedIds = routesToOpen.map(r => r.route_id);
  const availableRoutes = allRoutes.filter(r => !activeRoutes.includes(r.id) && !openedIds.includes(r.id));

  // Hub mode: unopened bands
  const hubBands = hubRoutes?.bands || [];
  const unopenedBands = hubBands.filter(b => !activeRoutes.includes(b.band) && !openedIds.includes(b.band));

  // Build a lookup for active route data from hub
  const hubRouteDataMap = {};
  if (hubRoutes) {
    hubBands.forEach(b => {
      if (b.chosen_index !== null && b.chosen_index !== undefined) {
        const opt = b.options[b.chosen_index];
        if (opt?.route_data) hubRouteDataMap[b.band] = opt.route_data;
      }
    });
  }

  // Build set of aircraft assigned across ALL routes (for exclusivity)
  const buildAssignedMap = (excludeRouteKey) => {
    const map = {};
    Object.entries(routeConfig).forEach(([rid, rc]) => {
      if (rid === excludeRouteKey) return;
      (rc.aircraft_ids || []).forEach(id => { map[id] = rid; });
    });
    routesToOpen.forEach((ro, idx) => {
      const key = `open_${idx}`;
      if (key === excludeRouteKey) return;
      (ro.aircraft_ids || []).forEach(id => { map[id] = ro.route_id || key; });
    });
    return map;
  };

  // Unassigned aircraft (for display)
  const assignedIds = new Set();
  Object.values(routeConfig).forEach(rc => (rc.aircraft_ids || []).forEach(id => assignedIds.add(id)));
  routesToOpen.forEach(ro => (ro.aircraft_ids || []).forEach(id => assignedIds.add(id)));
  const unassignedAircraft = aircraft.filter(a => !assignedIds.has(a.id));

  const updateRouteConfig = (routeId, field, value) => {
    const config = { ...routeConfig };
    if (!config[routeId]) config[routeId] = { aircraft_ids: [], flights_per_week: 7, class_config: "economy_only" };
    config[routeId] = { ...config[routeId], [field]: value };
    updateDecision("route_config", config);
  };

  const toggleAircraftForRoute = (routeId, acId, currentIds) => {
    const newIds = currentIds.includes(acId) ? currentIds.filter(id => id !== acId) : [...currentIds, acId];
    updateRouteConfig(routeId, "aircraft_ids", newIds);
  };

  const toggleAircraftForOpen = (idx, acId, currentIds) => {
    const newIds = currentIds.includes(acId) ? currentIds.filter(id => id !== acId) : [...currentIds, acId];
    updateRouteOpen(idx, "aircraft_ids", newIds);
  };

  const toggleClose = (routeId) => {
    const list = routesToClose.includes(routeId) ? routesToClose.filter(id => id !== routeId) : [...routesToClose, routeId];
    updateDecision("routes_close", list);
  };

  const addRouteOpen = (routeId, destIndex) => {
    if (routesToOpen.length >= 2) return;
    const opens = [...routesToOpen, {
      route_id: routeId,
      dest_index: destIndex ?? undefined,
      aircraft_ids: [],
      flights_per_week: 7,
      class_config: "economy_only",
    }];
    updateDecision("routes_open", opens);
  };

  const addLegacyRouteOpen = (routeId) => {
    if (routesToOpen.length >= 2) return;
    const route = allRoutes.find(r => r.id === routeId);
    if (!route) return;
    addRouteOpen(routeId);
  };

  const removeRouteOpen = (idx) => {
    const opens = [...routesToOpen];
    opens.splice(idx, 1);
    updateDecision("routes_open", opens);
  };

  const updateRouteOpen = (idx, field, value) => {
    const opens = [...routesToOpen];
    opens[idx] = { ...opens[idx], [field]: value };
    updateDecision("routes_open", opens);
  };

  const typeNames = { turboprop: "ATR72", used_narrowbody: "A320", new_narrowbody: "737M", widebody: "787" };

  return (
    <div className="space-y-6">
      {routesError && (
        <div
          className="rounded-lg border border-rose-300 bg-rose-50 p-4 flex flex-wrap items-center justify-between gap-3"
          role="alert"
          data-testid="routes-load-error"
        >
          <p className="flex items-start gap-2 text-sm text-rose-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {t("decisions.routesLoadError")}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-100"
            onClick={fetchRoutes}
            disabled={retrying}
            data-testid="routes-retry-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${retrying ? "animate-spin" : ""}`} />
            {t("decisions.retry")}
          </Button>
        </div>
      )}

      {/* Active Routes Configuration */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" /> {t("decisions.activeRoutesTitle")} ({activeRoutes.length})</CardTitle>
          <CardDescription>{t("decisions.activeRoutesDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {activeRoutes.map(routeId => {
            const route = hasHub ? (hubRouteDataMap[routeId] || {}) : (allRoutes.find(r => r.id === routeId) || {});
            const distCat = route.distance_category || hubBands.find(b => b.band === routeId)?.distance_category;
            const config = routeConfig[routeId] || { aircraft_ids: [], flights_per_week: 7, class_config: "economy_only" };
            const isClosing = routesToClose.includes(routeId);

            return (
              <div key={routeId} className={`p-4 rounded-lg border ${isClosing ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"}`} data-testid={`route-config-${routeId}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="font-semibold text-slate-800">{routeId}: {route.name || route.to || routeId}</span>
                    <span className="text-xs text-slate-500 ml-2">{route.distance_km}km | {distCat || "?"} | {route.market_size || ""}</span>
                  </div>
                  <Button size="sm" variant={isClosing ? "destructive" : "outline"} className="h-7 text-xs" onClick={() => toggleClose(routeId)} data-testid={`close-route-${routeId}`}>
                    {isClosing ? t("common.cancel") : t("decisions.route") + " ✕"}
                  </Button>
                </div>

                {!isClosing && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Aircraft Assignment — Multi-select (Dedicated Capacity) */}
                    <div>
                      <Label className="text-xs">{t("decisions.assignedAircraft")}</Label>
                      <AircraftMultiSelect
                        selectedIds={config.aircraft_ids || []}
                        allAircraft={aircraft}
                        distCat={distCat}
                        assignedElsewhere={buildAssignedMap(routeId)}
                        typeNames={typeNames}
                        onToggle={(acId) => toggleAircraftForRoute(routeId, acId, config.aircraft_ids || [])}
                        testIdPrefix={`route-aircraft-${routeId}`}
                      />
                    </div>
                    {/* Flights per Week */}
                    <div>
                      <div className="flex justify-between">
                        <Label className="text-xs">{t("decisions.flightsPerWeek")}</Label>
                        <span className="text-xs font-mono text-sky-600 font-semibold">{config.flights_per_week}</span>
                      </div>
                      <Slider value={[config.flights_per_week]} onValueChange={([v]) => updateRouteConfig(routeId, "flights_per_week", v)} min={1} max={21} step={1} className="mt-2" />
                      <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>1</span><span>7=daily</span><span>14=2x</span><span>21</span></div>
                    </div>
                    {/* Class Config */}
                    <div>
                      <Label className="text-xs">{t("decisions.classConfig")}</Label>
                      <Select value={config.class_config} onValueChange={val => updateRouteConfig(routeId, "class_config", val)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CLASS_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{t(c.labelKey)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {activeRoutes.length === 0 && <p className="text-slate-500 text-sm text-center py-4">{t("decisions.noActiveRoutes")}</p>}
        </CardContent>
      </Card>

      {/* Open New Routes */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle>{t("decisions.openNewRoutes")}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {routesToOpen.map((ro, idx) => {
            const route = hasHub
              ? (hubBands.find(b => b.band === ro.route_id)?.options?.[ro.dest_index]?.route_data || {})
              : (allRoutes.find(r => r.id === ro.route_id) || {});
            const distCat = route.distance_category || hubBands.find(b => b.band === ro.route_id)?.distance_category;
            return (
              <div key={ro.route_id || idx} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-emerald-800">{ro.route_id}: {route.name || route.to || "?"} (Slot: {formatCurrency(route.slot_fee || 0)})</span>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => removeRouteOpen(idx)}><X className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">{t("decisions.assignedAircraft")}</Label>
                    <AircraftMultiSelect
                      selectedIds={ro.aircraft_ids || []}
                      allAircraft={aircraft}
                      distCat={distCat}
                      assignedElsewhere={buildAssignedMap(`open_${idx}`)}
                      typeNames={typeNames}
                      onToggle={(acId) => toggleAircraftForOpen(idx, acId, ro.aircraft_ids || [])}
                      testIdPrefix={`open-route-aircraft-${idx}`}
                    />
                  </div>
                  <div>
                    <div className="flex justify-between"><Label className="text-xs">{t("decisions.flightsPerWeek")}</Label><span className="text-xs font-mono">{ro.flights_per_week}</span></div>
                    <Slider value={[ro.flights_per_week]} onValueChange={([v]) => updateRouteOpen(idx, "flights_per_week", v)} min={1} max={21} step={1} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("decisions.classConfig")}</Label>
                    <Select value={ro.class_config} onValueChange={val => updateRouteOpen(idx, "class_config", val)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{CLASS_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{t(c.labelKey)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Hub mode: show bands with 2 destination choices */}
          {hasHub && routesToOpen.length < 2 && unopenedBands.length > 0 && (
            <div>
              <Label className="text-xs mb-2 block">{t("decisions.selectRoute")} — {hubRoutes?.hub?.name}</Label>
              <div className="space-y-3">
                {unopenedBands.map(band => (
                  <div key={band.band} className="border border-slate-200 rounded-lg p-3" data-testid={`band-${band.band}`}>
                    <p className="text-xs font-semibold text-slate-500 mb-2">{band.band} ({band.distance_category})</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {band.options.map(opt => (
                        <button
                          key={opt.code}
                          onClick={() => addRouteOpen(band.band, opt.index)}
                          className="p-3 text-left border border-slate-200 rounded-lg hover:border-sky-300 hover:bg-sky-50 transition-colors"
                          data-testid={`open-hub-route-${band.band}-${opt.index}`}
                        >
                          <p className="font-medium text-sm text-slate-800">{opt.city} <span className="font-mono text-slate-400">({opt.code})</span></p>
                          <p className="text-xs text-slate-500">{opt.distance_km}km | Fare: {formatCurrency(opt.route_data?.market_avg_fare || 0)}</p>
                          <p className="text-xs text-slate-400">Demand: {(opt.route_data?.base_quarterly_demand || 0).toLocaleString()} | Slot: {formatCurrency(opt.route_data?.slot_fee || 0)}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legacy mode: show flat route list */}
          {!hasHub && routesToOpen.length < 2 && availableRoutes.length > 0 && (
            <div>
              <Label className="text-xs mb-2 block">{t("decisions.selectRoute")}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {availableRoutes.map(route => (
                  <button key={route.id} onClick={() => addLegacyRouteOpen(route.id)} className="p-3 text-left border border-slate-200 rounded-lg hover:border-sky-300 hover:bg-sky-50 transition-colors" data-testid={`open-route-${route.id}`}>
                    <p className="font-medium text-sm text-slate-800">{route.id}: {route.name}</p>
                    <p className="text-xs text-slate-500">{route.distance_km}km ({route.distance_category}) | {route.market_size} | {route.competition_level} comp</p>
                    <p className="text-xs text-slate-400">Slot fee: {formatCurrency(route.slot_fee)} | Avg fare: {formatCurrency(route.market_avg_fare)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unassigned Aircraft */}
      {unassignedAircraft.length > 0 && (
        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
          <Plane className="w-4 h-4 shrink-0" />
          <span className="text-sm">{unassignedAircraft.length} unassigned aircraft: {unassignedAircraft.map(a => `${a.id} (${typeNames[a.type]})`).join(", ")}</span>
        </div>
      )}
    </div>
  );
}
